import _ from 'lodash'
import CodeList from '@/modules/common/domain/codeList'
import DeliveryRoute from '@/modules/delivery/route/domain/deliveryRoute'
import i18n from '@/i18n'
import Item from '@/modules/item/domain/Item'
import Order from '@/modules/order/domain/order'

const MISSING_REGION_ID = '0'

const regionHeader = (regionItems: Array<Item>) => {
  return [
    i18n.message('order.deliveries.export.sequence.label'),
    i18n.message('partner.label'),
    i18n.message('company-branch.label'),
    ...regionItems.map((item, index) => `${index + 1}. ${item.name}`),
    i18n.message('common.note')
  ]
}

const regionOrderRow = (order: Order, regionItems: Array<Item>, index: number) => {
  const orderItemsById = order.items.reduce((map: { [key: number]: number | null | undefined }, orderItem) => {
    if (orderItem.item && orderItem.item.id != null) {
      map[orderItem.item.id] = orderItem.ordered
    }
    return map
  }, {})

  return [
    index + 1,
    order.subscriber ? order.subscriber.name : null,
    order.subscriberBranch ? order.subscriberBranch.name : i18n.message('company.address.label.short'),
    ...regionItems.map(item => orderItemsById[item.id!] ?? null),
    order.note || null
  ]
}

const regionTotalRow = (regionOrders: Array<Order>, regionItems: Array<Item>) => {
  return [
    i18n.message('common.total'),
    null,
    null,
    ...regionItems.map(item => _.sumBy(regionOrders, order => {
      const orderItem = order.items.find(value => value.item && value.item.id === item.id)
      return orderItem && orderItem.ordered ? orderItem.ordered : 0
    })),
    null
  ]
}

const collectRegionItems = (orders: Array<Order>) => {
  const items: Array<Item> = []
  const itemIds = new Set<number>()

  orders.forEach(order => {
    order.items.forEach(orderItem => {
      if (orderItem.item && orderItem.item.id != null && !itemIds.has(orderItem.item.id)) {
        itemIds.add(orderItem.item.id)
        items.push(orderItem.item)
      }
    })
  })

  return _.sortBy(items, item => item.name || '')
}

const routeOrders = (deliveryRoutes: Array<DeliveryRoute>) => {
  return {
    byCompany: _.chain(deliveryRoutes)
      .filter(deliveryRoute => _.isNil(deliveryRoute.companyBranch))
      .map(deliveryRoute => ({ [deliveryRoute.company!.id!]: deliveryRoute.order }))
      .reduce(_.extend)
      .value() || {},
    byCompanyBranch: _.chain(deliveryRoutes)
      .filter(deliveryRoute => !_.isNil(deliveryRoute.companyBranch))
      .map(deliveryRoute => ({ [deliveryRoute.companyBranch!.id!]: deliveryRoute.order }))
      .reduce(_.extend)
      .value() || {}
  }
}

const collectOrdersByRegion = (orders: Array<Order>, deliveryRoutes: Array<DeliveryRoute>) => {
  const routes = routeOrders(deliveryRoutes)
  return _(orders)
    .sortBy(order => {
      if (order.subscriberBranch) {
        return routes.byCompanyBranch[order.subscriberBranch.id!] || Number.MAX_VALUE
      }
      return routes.byCompany[order.subscriber!.id!] || Number.MAX_VALUE
    })
    .groupBy(order => order.subscriberAddress && order.subscriberAddress.region ? order.subscriberAddress.region.id : MISSING_REGION_ID)
    .value()
}

const filteredRegions = (regions: Array<CodeList>, ordersByRegion: { [key: string]: Array<Order> }) => {
  const usedRegions = regions.filter(region => !!ordersByRegion[region.id!])
  if (ordersByRegion[MISSING_REGION_ID]) {
    usedRegions.push(new CodeList({ id: MISSING_REGION_ID, label: i18n.message('region.unknown') }))
  }
  return usedRegions
}

const createExportData = (orders: Array<Order>, regions: Array<CodeList>, deliveryRoutes: Array<DeliveryRoute>) => {
  const ordersByRegion = collectOrdersByRegion(orders, deliveryRoutes)
  const exportRows: Array<Array<any>> = [
    [i18n.message('common.date') + ':', orders.length ? orders[0].date!.toDate() : null],
    [i18n.message('company-branch.label') + ':', orders.length ? orders[0].supplierBranch!.name : null],
    []
  ]

  filteredRegions(regions, ordersByRegion).forEach((region: CodeList) => {
    const regionOrders = ordersByRegion[region.id!]
    const regionItems = collectRegionItems(regionOrders)

    exportRows.push([i18n.message('region.label') + ':', region.label])
    exportRows.push(regionHeader(regionItems))
    regionOrders.forEach((order, index) => exportRows.push(regionOrderRow(order, regionItems, index)))
    exportRows.push(regionTotalRow(regionOrders, regionItems))
    exportRows.push([])
  })

  return exportRows
}

export default {
  createExportData
}
